import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({
  theme: 'system',
  isDarkMode: true,
  setTheme: () => {}
});

const getSystemPrefersDark = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true;
  }
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (e) {
    return true;
  }
};

const computeEffectiveDark = (preference) => {
  if (preference === 'light') return false;
  if (preference === 'dark') return true;
  return getSystemPrefersDark();
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'system';
    try {
      const stored = window.localStorage.getItem('dashboard-theme-preference');
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
      return 'system';
    } catch (e) {
      return 'system';
    }
  });

  const [isDarkMode, setIsDarkMode] = useState(() => computeEffectiveDark(theme));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const applyTheme = (preference) => {
      const effectiveDark = computeEffectiveDark(preference);
      setIsDarkMode(effectiveDark);

      try {
        window.localStorage.setItem('dashboard-theme-preference', preference);
        window.localStorage.setItem('dashboard-theme', effectiveDark ? 'dark' : 'light');
      } catch (e) {}

      const root = document.documentElement;
      if (effectiveDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }

      try {
        const event = new CustomEvent('dashboard-theme-change', {
          detail: { isDarkMode: effectiveDark }
        });
        window.dispatchEvent(event);
      } catch (e) {}
    };

    applyTheme(theme);

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleMediaChange = () => {
      if (theme === 'system') {
        applyTheme('system');
      }
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleMediaChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleMediaChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleMediaChange);
      }
    };
  }, [theme]);

  const setTheme = (nextTheme) => {
    setThemeState((prev) => {
      const allowed = nextTheme === 'light' || nextTheme === 'dark' || nextTheme === 'system';
      return allowed ? nextTheme : prev;
    });
  };

  const value = React.useMemo(
    () => ({ theme, isDarkMode, setTheme }),
    [theme, isDarkMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
